import { AsYouType, CountryCode, getCountryCallingCode, PhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { ChangeEvent, FocusEvent, ForwardedRef, forwardRef, useEffect, useRef, useState } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { useText } from '../../../utils/hooks/use_text';
import { CharacterCount } from '../../character_count';
import { theme as commonTheme } from '../common_input.theme';
import type { InputProps } from '../index';
import { Description, HelpText, Input, Label } from '../index';
import { useInput } from '../use_input.hook';
import { getPhoneNumberPlaceholder } from './get_phone_number_placeholder';
import { InputPhoneNumberCountrySelect } from './input_phone_number_country_select.component';

export interface InputPhoneNumberProps extends InputProps {
	/**
	 * The label of the input field.
	 */
	label: string;

	/**
	 * Use to visually hide the label.
	 */
	hideLabel?: boolean;
	description?: string;

	name?: string;

	/**
	 * Use to add a descriptive text below the input field.
	 */
	helperText?: string;

	/**
	 * It's mandatory if `invalid` is set to `true` and only visible in that case
	 * too. The purpose with this is to provide a explanation why the input field is
	 * `invalid`. This text will replace the `helperText`, right below the text
	 * area.
	 */
	invalidText?: string;

	/**
	 * The maximum amount of characters allowed by the input
	 * Keep in mind that you still need to handle validation with `invalid` and `invalidText`
	 */
	characterCount?: number;

	/**
	 * This determines if the character-count should be shown even if the
	 * `characterCount` value has not been exceeded
	 */
	alwaysShowCharacterCount?: boolean;
	/**
	 * The initial country. The default placeholder follows the selected country's calling code.
	 */
	countryCode?: CountryCode;

	options?: RegisterOptions;
}

const elements = {
	container: styled.fieldset`
		all: unset;
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
	`,
	label: styled(Label)`
		margin-bottom: ${commonTheme.getValue('labelMarginBottom')};
	`,
	description: styled(Description)`
		margin-bottom: ${commonTheme.getValue('descriptionMarginBottom')};
	`,
	inputContainer: styled.div`
		position: relative;
	`,
	countrySelect: styled.div`
		position: absolute;
		top: ${coreTokens.spacing.x0_5};
		left: ${coreTokens.spacing.x0_5};
		z-index: 1;
		width: 64px;
		height: calc(100% - ${coreTokens.spacing.x0_5} * 2);
		display: flex;
		align-items: center;
		color: ${commonTheme.getValue('baseTextColor')};
	`,
	input: styled(Input)`
		padding-left: calc(64px + ${coreTokens.spacing.x1});
	`,
	additionalInformation: styled.span`
		display: flex;
		justify-content: space-between;
	`,
	footer: styled.div`
		display: flex;
		justify-content: space-between;
		gap: ${commonTheme.getValue('helpTextGap')};
	`,
};

export const CellulaInputPhoneNumberRoot = forwardRef(function CellulaInputPhoneNumberRoot(
	{
		id,
		label,
		hideLabel,
		name,
		required,
		invalid,
		invalidText,
		helperText,
		readOnly,
		onChange,
		onBlur,
		description,
		defaultValue,
		characterCount,
		alwaysShowCharacterCount,
		countryCode: defaultCountryCode,
		value,
		optional,
		...rest
	}: InputPhoneNumberProps,
	ref: ForwardedRef<HTMLInputElement>,
): JSX.Element {
	const [inputValue, setInputValue] = useState<string>(defaultValue?.toString() ?? value?.toString() ?? '');
	const [countryCode, setCountryCode] = useState<CountryCode | undefined>(defaultCountryCode);
	const [asYouType, setAsYouType] = useState<AsYouType>(new AsYouType(countryCode));
	const hiddenRef = useRef<HTMLInputElement>(null);
	const staticTexts = useText();
	const inputLabel = staticTexts('Component.InputPhoneNumber.InputLabel');
	const phonePlaceholder = getPhoneNumberPlaceholder(countryCode);

	function replaceCountryCallingCode(number: string, newCountryCode: CountryCode): PhoneNumber | null {
		try {
			const phoneNumber = parsePhoneNumber(number, countryCode ?? newCountryCode);
			if (phoneNumber.country === newCountryCode) {
				return phoneNumber;
			}

			const newCountryCallingCode = getCountryCallingCode(newCountryCode);

			return parsePhoneNumber(phoneNumber.nationalNumber ? `+${newCountryCallingCode}${phoneNumber.nationalNumber}` : number);
		} catch {
			// Do nothing
		}

		return null;
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function whenMountTryToSetCountryCodeFromDefaultValue() {
			if (typeof defaultValue !== 'string') {
				return;
			}

			// We are using try-catch here because the `parsePhoneNumber` function
			// throws an error if it couldn't parse the phone number. It's okay
			// because we are only doing this to try set the country code. If we
			// couldn't parse the phone number, we'll just not set country code.
			try {
				const phoneNumber = parsePhoneNumber(defaultValue?.toString() ?? '', defaultCountryCode);
				setInputValue(phoneNumber.format('INTERNATIONAL'));
				setCountryCode(phoneNumber.country ?? undefined);
			} catch {
				// Do nothing, it's okay to fail sometimes. We're only humans...
				// 🤖 - or are we?
			}
		},
		// Hack. We only want this to run once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(
		function updateCountryCode() {
			setAsYouType(new AsYouType(countryCode));
		},
		[countryCode],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function setHiddenInputValue() {
			if (hiddenRef.current != null) {
				asYouType.reset();
				asYouType.input(inputValue);
				hiddenRef.current.value = asYouType.getNumberValue() ?? inputValue;
				onChange?.({ target: hiddenRef.current } as ChangeEvent<HTMLInputElement>);
			}
		},
		// TODO: See if asYouType can be added to the dependency array.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[inputValue, onChange],
	);

	function handleCountryCodeChange(countryCode: CountryCode): void {
		const phoneNumber = replaceCountryCallingCode(inputValue, countryCode);

		if (phoneNumber) {
			setInputValue(phoneNumber.format('INTERNATIONAL'));
		} else if (inputValue === '') {
			setInputValue(`+${getCountryCallingCode(countryCode)}`);
		}

		setCountryCode(countryCode);
	}

	function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
		setInputValue(event.target.value);
	}

	function handleBlur(event: FocusEvent<HTMLInputElement>): void {
		asYouType.reset();
		const newFormattedNumber = asYouType.input(inputValue);
		const phoneNumber = asYouType.getNumber();

		if (phoneNumber?.country && phoneNumber.country !== countryCode) {
			handleCountryCodeChange(phoneNumber.country);
		} else {
			setInputValue(newFormattedNumber);
		}

		onBlur?.(event);
	}

	const {
		props: inputProps,
		helpTextProps,
		labelProps,
		characterCountProps,
		descriptionProps,
	} = useInput<HTMLInputElement>(
		{
			id,
			name,
			label,
			hideLabel,
			description,
			required,
			invalid,
			invalidText,
			helperText,
			readOnly,
			onChange: handleInputChange,
			onBlur,
			defaultValue,
			characterCount,
			alwaysShowCharacterCount,
			optional,
		},
		ref,
	);

	return (
		<elements.container>
			{/** biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed */}
			<elements.label type='legend' {...labelProps} htmlFor={undefined} />
			<elements.description {...descriptionProps} />

			<elements.inputContainer>
				<elements.countrySelect>
					<InputPhoneNumberCountrySelect onValueChange={handleCountryCodeChange} value={countryCode} readOnly={readOnly} />
				</elements.countrySelect>
				<elements.input
					{...inputProps}
					placeholder={phonePlaceholder}
					{...rest}
					value={inputValue}
					onChange={handleInputChange}
					onBlur={handleBlur}
					aria-label={inputLabel}
				/>
			</elements.inputContainer>

			<input type='hidden' ref={hiddenRef} />

			<elements.footer>
				<HelpText {...helpTextProps} />

				<CharacterCount value={inputValue} {...characterCountProps} />
			</elements.footer>
		</elements.container>
	);
});
